import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { IUser } from '@pdma/shared-types';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'pdma_token';
  private readonly USER_KEY = 'pdma_user';

  private readonly _user = signal<IUser | null>(this.loadUser());
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());
  readonly hasTeam = computed(() => !!this._user()?.teamId);
  readonly isAdmin = computed(() => {
    const u = this._user();
    return u?.role === 'admin' || u?.role === 'comms_admin';
  });
  readonly isTeamLead = computed(() => this._user()?.role === 'team_lead');

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  /** Called by AuthCallbackComponent after SSO redirect. Exchanges the one-time code for a JWT. */
  async exchangeCodeForToken(code: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<{ accessToken: string; user: IUser }>('/api/auth/exchange', { code }),
    );
    localStorage.setItem(this.TOKEN_KEY, res.accessToken);
    this._user.set(res.user);
    localStorage.setItem(this.USER_KEY, JSON.stringify(res.user));
  }

  async setTeam(teamId: string): Promise<void> {
    const user = await firstValueFrom(
      this.http.patch<IUser>('/api/auth/me/team', { teamId }),
    );
    this._user.set(user);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  async refreshProfile(): Promise<void> {
    const user = await firstValueFrom(this.http.get<IUser>('/api/auth/me'));
    this._user.set(user);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this._user.set(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private loadUser(): IUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}
